import { Box, Button, Stack, Typography } from '@mui/material';
import { type TRPCClientErrorBase } from '@trpc/client';
import { type UseTRPCQueryResult } from '@trpc/react-query/shared';
import { type TRPCDefaultErrorShape } from '@trpc/server';

import { url } from '../../appRouter/url';
import { type GetVeranstaltungenByAkaProduktIdsOutput, sapStatus, type ThemaFromThemaRouter } from '../../dtos';
import { formatDate, getVeranstaltungEnd, getVeranstaltungStart, getVeranstaltungTrainers } from '../../utils';
import { AccordionWithHeading } from './AccordionWithHeading';
import { useOnlinePraesenzFilter } from './hooks/useOnlinePraesenzFilter';
import { usePaginatedList } from './hooks/usePaginatedList';
import { Link } from './Link';
import { SelectableChipGroup } from './SelectableChipGroup';
import { TrpcLoadingInfo } from './TrpcLoadingInfo';
import { VeranstaltungOrt } from './VeranstaltungOrt';
import { VeranstaltungTrainer } from './VeranstaltungTrainer';

type VeranstaltungenListProps = {
  readonly veranstaltungen: UseTRPCQueryResult<
    GetVeranstaltungenByAkaProduktIdsOutput,
    TRPCClientErrorBase<TRPCDefaultErrorShape>
  >;
  readonly thema: ThemaFromThemaRouter;
  readonly linkedThema?: ThemaFromThemaRouter;
};

export const VeranstaltungenList: React.FC<VeranstaltungenListProps> = ({
  veranstaltungen,
  thema,
  linkedThema,
}: VeranstaltungenListProps) => {
  const { displayedCount, showMore } = usePaginatedList();

  const { allOptions, selectedOptions, toggleOption } = useOnlinePraesenzFilter(thema, linkedThema);

  const getVeranstaltungDateRange = (veranstaltung: GetVeranstaltungenByAkaProduktIdsOutput[number]) => {
    const start = getVeranstaltungStart(veranstaltung);
    const end = getVeranstaltungEnd(veranstaltung);

    return (
      <Typography
        variant="labelLight"
        color={veranstaltung.sapStatus === sapStatus.ABGESAGT ? 'textTertiary' : undefined}
        sx={{ textDecoration: veranstaltung.sapStatus === sapStatus.ABGESAGT ? 'line-through' : undefined }}
      >
        {formatDate(start) + ' - ' + formatDate(end, true)}
      </Typography>
    );
  };

  const getVeranstaltungOrt = (veranstaltung: GetVeranstaltungenByAkaProduktIdsOutput[number]) =>
    veranstaltung.onlineTool ??
    Array.from(new Set(veranstaltung.raumBlockungen.map((blockung) => blockung.raum.location.kuerzel))).join(', ');

  const getFilteredVeranstaltungen = () =>
    veranstaltungen.data
      ? veranstaltungen.data.filter((veranstaltung) => selectedOptions.includes(veranstaltung.thema.akaProduktId))
      : [];

  return (
    <AccordionWithHeading heading="Veranstaltungen" bordered={false}>
      <TrpcLoadingInfo trpcQuery={veranstaltungen} entity="Veranstaltungen">
        {thema.verknuepfungBlopUndPraesenzseminar && (
          <SelectableChipGroup
            options={allOptions}
            selectedOptions={selectedOptions}
            onSelectionChange={toggleOption}
          />
        )}
        <Stack direction="column" gap={1.5} pt={1.5}>
          {getFilteredVeranstaltungen().length === 0 && (
            <Typography variant="labelLight" color="textTertiary" textAlign="center" pt={1}>
              Wähle aus, ob du Präsenz- und/oder Online-Veranstaltungen sehen möchtest.
            </Typography>
          )}
          {getFilteredVeranstaltungen().length > 0 &&
            getFilteredVeranstaltungen()
              .slice(0, displayedCount)
              .map((veranstaltung) => (
                <Stack
                  direction="row"
                  key={veranstaltung.akaVeranstaltungId}
                  sx={{
                    backgroundColor:
                      veranstaltung.sapStatus === sapStatus.ABGESAGT ? 'surface.background' : 'surface.brandLight',
                    p: 1,
                    borderRadius: '4px',
                    border: (theme) =>
                      veranstaltung.sapStatus === sapStatus.ABGESAGT
                        ? `1px solid ${theme.palette.border.subtle}`
                        : undefined,
                  }}
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%"
                >
                  <Stack direction="column" spacing={0.5} flexGrow={1} width="calc(100% - 24px)">
                    {getVeranstaltungDateRange(veranstaltung)}
                    <Box display="grid" gap={0.5} gridTemplateColumns="1fr 2fr">
                      <VeranstaltungOrt
                        online={Boolean(veranstaltung.onlineTool)}
                        orte={[getVeranstaltungOrt(veranstaltung)]}
                      />
                      <VeranstaltungTrainer trainers={getVeranstaltungTrainers(veranstaltung.experteBlockungen)} />
                    </Box>
                  </Stack>
                  <Link
                    url={url.veranstaltung.details(veranstaltung.akaVeranstaltungId, veranstaltung.veranstaltungSapId)}
                    newTab
                  />
                </Stack>
              ))}
          {getFilteredVeranstaltungen().length > displayedCount && (
            <Button onClick={showMore} sx={{ color: 'text.primary' }}>
              Mehr anzeigen
            </Button>
          )}
        </Stack>
      </TrpcLoadingInfo>
    </AccordionWithHeading>
  );
};
